import { useEffect, useMemo, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import { Composer } from "./Composer";
import { TrustLine } from "./TrustLine";
import { EmptyState } from "./EmptyState";
import { CopilotThinking } from "./CopilotThinking";
import { BudgetNotice } from "./BudgetNotice";
import { RenderCard } from "./cards/RenderCard";
import { CopilotAssistantText } from "./CopilotAssistantText";
import { AllowanceLine, ExhaustedComposer } from "./AllowanceLine";
import { detectBudgetNotice } from "../../lib/pro-budget-notice";
import { suggestionChipsForRoute } from "../../lib/pro-chat-suggestions";
import type { GetProToken } from "../../hooks/useProToken";
import type { AsyncResult } from "../../hooks/useProData";
import { useProChatStore } from "../../stores/proChat";
import type { UseProChatSocket } from "../../hooks/useProChatSocket";
import type { MessageContext } from "../../lib/pro-agent-types";
import type { AssistantAllowanceVM } from "../../lib/view-model";

// The dock's shared inner content — reused by the docked panel, the
// overlay drawer, and the full-page /assistant surface (plan 188 W2-M8,
// task 802). Composer placeholder / route context vary by host.
export function ChatDockPanel({
  socket,
  getToken,
  pathname,
  clientProfileId,
  composerPlaceholder,
  composerBordered = true,
  allowance,
  onUpgrade,
}: {
  socket: UseProChatSocket;
  getToken: GetProToken;
  pathname: string;
  clientProfileId?: string;
  composerPlaceholder?: string;
  /** Full-page surface drops the composer's top divider (it already has the
   * page rail/column chrome). Defaults to true for the dock/drawer. */
  composerBordered?: boolean;
  /**
   * SILL-521 M2 cluster 4 — the copilot's daily allowance, owned by the
   * mount-point host (`ProChatDock` / `AssistantPage`) via
   * `useAssistantAllowance()` and threaded down as a prop (mirrors
   * `getToken`/`socket`) rather than fetched here directly, so existing
   * ChatDockPanel unit tests that render with no router/Clerk context keep
   * working unchanged. Absent (undefined) renders nothing new — same as the
   * `pro` tier, a fail-safe default while the data is still loading.
   */
  allowance?: AsyncResult<AssistantAllowanceVM>;
  /** Navigates to `/settings#billing` — the post-exhaustion CTA target. */
  onUpgrade?: () => void;
}) {
  const messages = useProChatStore((s) => s.messages);
  const isStreaming = useProChatStore((s) => s.isStreaming);
  const activeToolCallIds = useProChatStore((s) => s.activeToolCallIds);
  const currentToolName = useProChatStore((s) => s.currentToolName);
  const lastError = useProChatStore((s) => s.lastError);
  const hasUnreadAdvisorMessage = useProChatStore(
    (s) => s.hasUnreadAdvisorMessage,
  );
  const addMessage = useProChatStore((s) => s.addMessage);
  const markAdvisorMessagesRead = useProChatStore(
    (s) => s.markAdvisorMessagesRead,
  );
  const setStreaming = useProChatStore((s) => s.setStreaming);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // jsdom (unit tests) doesn't implement Element.scrollTo; guard rather
    // than stub it globally for every test file.
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight });
  }, [messages, activeToolCallIds, isStreaming]);

  useEffect(() => {
    if (hasUnreadAdvisorMessage) {
      markAdvisorMessagesRead();
    }
  }, [hasUnreadAdvisorMessage, markAdvisorMessagesRead]);

  // SILL-521 M2 cluster 4: refetch the allowance once a turn actually
  // completes (isStreaming true→false), so the remaining-turns line and the
  // post-exhaustion swap reflect the just-sent message without a page
  // reload. `refresh` is the useAsync-memoized stable callback (not the
  // `allowance` object, which is a fresh literal every render) — depending
  // on the object itself would refire this on every unrelated re-render.
  const wasStreamingRef = useRef(isStreaming);
  const refreshAllowance = allowance?.refresh;
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      void refreshAllowance?.();
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, refreshAllowance]);

  const chips = useMemo(() => suggestionChipsForRoute(pathname), [pathname]);

  function send(text: string) {
    if (isStreaming) return;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const context: MessageContext = { route: pathname, timezone };
    if (clientProfileId) {
      context.client_profile_id = clientProfileId;
    }
    // Optimistically echo the pro's message into its own right-aligned bubble
    // and flip to streaming — the WS only streams assistant frames back (no
    // user echo), so without this the input vanishes and the dock looks dead
    // until the first token lands. Mirrors the consumer advisor handleSubmit.
    addMessage({
      id: `user-${Date.now()}`,
      role: "user",
      text,
      createdAt: new Date().toISOString(),
    });
    setStreaming(true);
    socket.send({ type: "message", text, context });
  }

  const lastAdvisorText =
    [...messages].reverse().find((m) => m.role === "advisor")?.text ?? "";
  const budgetNotice = detectBudgetNotice(lastAdvisorText);
  const isWorking = activeToolCallIds.length > 0;
  // ONE continuously-mounted "working" affordance for the whole pre-text phase
  // of a turn (and during any post-text tool call), so the dock never flickers
  // between a dots bubble and a shimmer box, or blanks out in the gaps between
  // a multi-tool turn's calls (SILL-459). It hides the moment assistant text is
  // streaming with no tool running — the message bubble takes over.
  const lastMessage = messages[messages.length - 1];
  const streamingAdvisorHasText =
    lastMessage?.role === "advisor" && Boolean(lastMessage.text);
  const isThinking = isStreaming && (isWorking || !streamingAdvisorHasText);
  // EMPTY only when there is truly nothing else to show — a socket-level
  // error or an in-flight tool call on a zero-message turn still needs to
  // render its own state rather than the suggestion chips.
  const isEmpty =
    messages.length === 0 && !isWorking && !budgetNotice && !lastError;

  const allowanceData = allowance?.data ?? null;
  const isFreeTier = allowanceData?.tier === "free";
  const isExhausted = isFreeTier && allowanceData!.remaining <= 0;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <ScrollArea ref={scrollRef} className="flex-1 px-3 py-3">
        {isEmpty ? (
          <EmptyState chips={chips} onSelectChip={send} />
        ) : (
          <div className="space-y-3">
            {messages.map((message) => {
              // Hold this message's render cards until its turn finishes
              // streaming, so a card doesn't pop in mid-generation while text
              // is still arriving. Once isStreaming flips false the cards mount
              // together and fade in (copilot-card-enter) at the end of the
              // message.
              const holdRenders = isStreaming && message.id === lastMessage?.id;
              return (
                <div key={message.id} className="space-y-2">
                  {message.text && (
                    <div
                      data-testid={`copilot-message-${message.role}`}
                      className={
                        message.role === "user"
                          ? "ml-auto w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-primary px-3 py-2 text-sm text-primary-foreground"
                          : "copilot-msg-enter mr-auto max-w-[92%] break-words rounded-2xl bg-muted px-3 py-2 text-fg"
                      }
                    >
                      {message.role === "user" ? (
                        message.text
                      ) : (
                        <CopilotAssistantText
                          text={message.text}
                          streaming={
                            isStreaming && message.id === lastMessage?.id
                          }
                        />
                      )}
                    </div>
                  )}
                  {!holdRenders &&
                    message.renders?.map((render, i) => (
                      <div
                        key={`${message.id}-render-${i}`}
                        className="copilot-card-enter"
                      >
                        <RenderCard
                          payload={render}
                          getToken={getToken}
                          onDismiss={() => send("I discarded that draft.")}
                        />
                      </div>
                    ))}
                </div>
              );
            })}

            {isThinking && <CopilotThinking toolName={currentToolName} />}

            {!isWorking && budgetNotice && (
              <BudgetNotice
                writesThisTurn={budgetNotice.writesThisTurn}
                onContinue={() => send("continue")}
              />
            )}

            {lastError && (
              <p
                role="alert"
                data-testid="copilot-error-banner"
                className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-2.5 py-2 text-xs text-destructive"
              >
                <AlertTriangle size={13} aria-hidden />
                {lastError.error}
              </p>
            )}
          </div>
        )}
      </ScrollArea>
      {/* SILL-521 M2 cluster 4: free-tier remaining-turns line + the
          post-exhaustion CTA that REPLACES the composer (one of the three
          sanctioned upsell surfaces — an event-moment affordance, never a
          new modal/banner). Pro tier (or allowance not yet loaded) renders
          nothing new here — the 50/day cap is generous, a counter is noise. */}
      {isFreeTier && !isExhausted && allowanceData && (
        <AllowanceLine remaining={allowanceData.remaining} limit={allowanceData.limit} />
      )}
      {isExhausted && allowanceData ? (
        <ExhaustedComposer onUpgrade={onUpgrade} />
      ) : (
        <Composer
          onSend={send}
          disabled={isStreaming}
          placeholder={composerPlaceholder}
          bordered={composerBordered}
        />
      )}
      <TrustLine />
    </div>
  );
}
